import { AlertTriangle, ExternalLink, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SetupGuideProps } from "../../types";
import { useEffect, useState } from "react";
import { useSourceSpec, useDestinationSpec, useCreateSource, useCreateDestination, ConnectorSpec } from "../../../views/marketing-pages/connectors/connector/airbyte-api";
import { DynamicSchemaForm, defaultsForSchema, validateSchema } from "./schema-form";

// Real setup form, driven entirely by the connector's own live JSON-schema
// config spec (GET .../source_definition_specifications/get or
// .../destination_definition_specifications/get — see airbyte-api.ts), not a
// hardcoded per-connector switch. Only reachable for a connector the catalog
// marked enabled (connector.definitionId set) — see sources.tsx/destinations.tsx.
export const SetupGuide: React.FC<SetupGuideProps> = ({ connector, onClose, onTestConnection, onSaved, existingConfiguration, existingName }) => {
  const isSource = connector.type === "Source";
  const { loading: specLoading, getSpec: getSourceSpec } = useSourceSpec();
  const { loading: destSpecLoading, getSpec: getDestinationSpec } = useDestinationSpec();
  const { loading: creatingSource, create: createSource } = useCreateSource();
  const { loading: creatingDestination, create: createDestination } = useCreateDestination();

  const [spec, setSpec] = useState<ConnectorSpec | null>(null);
  const [specError, setSpecError] = useState<string | null>(null);
  const [name, setName] = useState(existingName || connector.name);
  const [values, setValues] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setName(existingName || connector.name);
    setSpec(null);
    setSpecError(null);
    setSaved(false);
    setErrors({});
    if (!connector.definitionId) return;
    let cancelled = false;
    (async () => {
      const { spec: result, error } = isSource ? await getSourceSpec(connector.definitionId!) : await getDestinationSpec(connector.definitionId!);
      if (cancelled) return;
      if (result) {
        setSpec(result);
        // Real saved values (GET /{org}/sources|destinations' own
        // `.configuration`) win over the schema's bare defaults, so reopening
        // an already-connected source/destination shows what's actually
        // configured instead of a blank form.
        setValues({ ...defaultsForSchema(result.connectionSpecification), ...(existingConfiguration || {}) });
      } else {
        setSpecError(error);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connector.id, connector.definitionId, existingConfiguration, existingName]);

  const loadingSpec = isSource ? specLoading : destSpecLoading;
  const saving = isSource ? creatingSource : creatingDestination;

  const handleSave = async () => {
    if (!connector.definitionId || !spec) return;
    // Confirmed live (Facebook Marketing's real spec): required fields like
    // `account_ids` (array, minItems: 1) were never checked before this hit
    // the API, so a left-blank required field surfaced as a raw Airbyte
    // JSON-schema error instead of a clear inline one.
    const validationErrors = validateSchema(spec.connectionSpecification, values);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Fill in the required fields highlighted below before connecting.");
      return;
    }
    setErrors({});
    const { source, destination, error } = isSource
      ? { ...(await createSource({ name, definitionId: connector.definitionId, configuration: values })), destination: undefined }
      : { ...(await createDestination({ name, definitionId: connector.definitionId, configuration: values })), source: undefined };
    if (source || destination) {
      setSaved(true);
      toast.success(`${name} connected.`);
      onTestConnection?.(true);
      onSaved?.();
    } else {
      toast.error(error || `Couldn't connect ${name}.`);
      onTestConnection?.(false);
    }
  };

  return (
    <div className="w-full h-full bg-gray-900 text-white overflow-y-auto">
      <div className="flex items-center justify-between p-6 border-b border-gray-700">
        <div className="flex items-center space-x-3">
          <span className="text-2xl">{connector.icon}</span>
          <h2 className="text-xl font-semibold">Setup Guide</h2>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => window.open(`https://docs.airbyte.com/integrations/${isSource ? "sources" : "destinations"}/${connector.name.toLowerCase().replace(/\s+/g, "-")}`, "_blank")}
            className="flex items-center space-x-2 px-3 py-1 bg-gray-800 rounded-md hover:bg-gray-700"
          >
            <ExternalLink className="h-4 w-4" />
            <span className="text-sm">Open full docs</span>
          </button>
          <button onClick={onClose} className="p-1 hover:bg-gray-800 rounded-md">
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">{connector.name}</h1>
        </div>

        {existingConfiguration && (
          <div className="bg-indigo-900/30 border border-indigo-600/50 rounded-lg p-4">
            <p className="text-gray-200 text-sm">
              Showing {name}'s current configuration. There's no live-edit on this connector yet — saving below creates a <strong>new</strong> connection rather
              than updating this one. Use Disconnect first if you want to replace it.
            </p>
          </div>
        )}

        {connector.disabled || !connector.definitionId ? (
          <div className="bg-amber-900/30 border border-amber-600/50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-gray-200">
                {connector.name} isn't connectable yet on this workspace — it's listed for visibility but has no working provider configured here.
              </p>
            </div>
          </div>
        ) : loadingSpec && !spec ? (
          <p className="flex items-center gap-2 text-sm text-gray-400">
            <Loader2 size={14} className="animate-spin" /> Loading the real configuration fields for {connector.name}…
          </p>
        ) : specError ? (
          <div className="bg-rose-900/30 border border-rose-600/50 rounded-lg p-4">
            <p className="text-gray-200">{specError}</p>
          </div>
        ) : spec ? (
          <>
            <div>
              <h3 className="text-xl font-semibold text-white mb-6">Configuration</h3>
              {/* Was `dark:bg-gray-100` — still near-white in dark mode, so the
                  schema-driven fields below (which do switch to a dark
                  `bg-gray-900` via their own shared inputCls) ended up as dark
                  input boxes floating on a near-white card. Dark mode now
                  gets a real dark card to match. */}
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <div className="mb-3.5">
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">{isSource ? "Source" : "Destination"} name</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20"
                  />
                </div>
                <DynamicSchemaForm
                  schema={spec.connectionSpecification}
                  values={values}
                  errors={errors}
                  onChange={(patch) => {
                    setValues((v) => ({ ...v, ...patch }));
                    setErrors((e) => {
                      const next = { ...e };
                      Object.keys(patch).forEach((k) => {
                        delete next[k];
                        Object.keys(next).forEach((ek) => {
                          if (ek.startsWith(`${k}.`)) delete next[ek];
                        });
                      });
                      return next;
                    });
                  }}
                />
              </div>
            </div>
          </>
        ) : null}

        {saved && (
          <div className="bg-green-900/30 border border-green-600/50 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              <span className="text-green-400 font-medium">Connected — {name} was created for real.</span>
            </div>
          </div>
        )}

        {spec && (
          <div className="flex space-x-4 pt-6 border-t border-gray-700">
            <button
              onClick={handleSave}
              disabled={saving || !name.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:bg-blue-400 disabled:cursor-not-allowed"
            >
              {saving ? "Connecting…" : existingConfiguration ? "Create New Connection" : `Connect ${isSource ? "Source" : "Destination"}`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
